/**
 * `<caos-test-run-grid>` — pick what to run out of a hundred-odd methods, then run it.
 * (Catalog: Developer surfaces › TestRunGrid — choose test classes and methods for a run, with the run options and the abort.)
 *
 * THE SCALE IS THE DESIGN PROBLEM. The reference surface carries 103 checkboxes on first load, and a
 * flat list of 103 checkboxes is not a control anybody uses — they tick "all" and wait eleven
 * minutes. So the unit of selection is the CLASS, with its methods under it, and a class is
 * tri-state: all, none, or some. Picking three methods out of one class is then two clicks rather
 * than a hundred, and the count at the top says what those clicks actually selected.
 *
 * "RE-RUN WHAT FAILED" IS THE MOST-USED CONTROL ON THE SCREEN and it needs no new machinery: hand in
 * `failed` and it selects exactly those. It is disabled with nothing failed rather than hidden,
 * because its absence would otherwise read as "this run had no failures" on a screen that has not
 * run anything yet.
 *
 * THE THREE RUN OPTIONS travel WITH the run rather than being remembered here. Open-when-finished,
 * skip-coverage and always-asynchronous change what the application does with a run it has been
 * asked for, so they ride on the event. A component holding them would be holding somebody else's
 * preferences.
 *
 * ABORT IS NOT THE RUN BUTTON DISABLED. A run that cannot be stopped is a run people avoid starting,
 * so while `running` the primary control becomes Abort and raises its own event.
 *
 * Styling seams: `::part(grid)`, `::part(summary)`, `::part(options)`, `::part(option)`,
 * `::part(classes)`, `::part(class)`, `::part(class-toggle)`, `::part(methods)`, `::part(method)`,
 * `::part(actions)`, `::part(run)`, `::part(rerun-failed)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';

/** One test class and the methods in it. */
export interface CaosTestClass {
  readonly name: string;
  readonly methods: readonly string[];
}

/** What the caller asks the application to do with the run. */
export interface CaosTestRunOptions {
  /** Show the run as soon as it finishes. */
  readonly openWhenFinished: boolean;
  /** Do not gather coverage — faster, and the reason somebody turns it off. */
  readonly skipCoverage: boolean;
  /** Always run asynchronously, even for a selection small enough to run inline. */
  readonly alwaysAsynchronous: boolean;
}

/** How a class's checkbox stands. */
export type CaosClassSelection = 'all' | 'some' | 'none';

const sheet = makeSheet(`
  :host { display: block; }

  .grid { display: flex; flex-direction: column; gap: var(--caos-space-3); }

  .summary { font-family: var(--caos-font); font-size: 12.5px; color: var(--caos-color-text-muted); }
  .summary strong { color: var(--caos-color-text); font-weight: 600; }

  .options { display: flex; gap: var(--caos-space-4); flex-wrap: wrap; }
  .option {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: var(--caos-font);
    font-size: 12.5px;
    color: var(--caos-color-text);
  }

  .classes {
    display: flex;
    flex-direction: column;
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-sm);
    max-height: 340px;
    overflow-y: auto;
  }

  .class {
    border-bottom: 1px solid var(--caos-color-border);
  }
  .class:last-child { border-bottom: 0; }

  .class-toggle {
    display: flex;
    align-items: center;
    gap: var(--caos-space-2);
    padding: 6px 10px;
    font-family: var(--caos-font);
    font-size: 13px;
    color: var(--caos-color-text);
    background: var(--caos-color-surface-2);
  }
  .class-toggle .count { color: var(--caos-color-text-muted); font-size: 12px; }

  .methods { display: flex; flex-direction: column; }
  .method {
    display: flex;
    align-items: center;
    gap: var(--caos-space-2);
    padding: 4px 10px 4px 30px;
    font-family: var(--caos-font-mono);
    font-size: 12px;
    color: var(--caos-color-text);
  }
  .method[data-failed="yes"] { color: var(--caos-color-danger-text); }

  input[type="checkbox"]:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }

  .actions { display: flex; gap: var(--caos-space-2); align-items: center; flex-wrap: wrap; }
  .actions button {
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-sm);
    background: var(--caos-color-surface);
    color: var(--caos-color-text);
    font-family: var(--caos-font);
    font-size: 13px;
    padding: 6px 14px;
    cursor: pointer;
  }
  .actions button:disabled { opacity: var(--caos-state-disabled-opacity); cursor: not-allowed; }
  .actions button:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }
  .actions button.run:not(:disabled) {
    border-color: var(--caos-color-accent);
    background: var(--caos-color-accent-fill);
    color: var(--caos-color-accent-contrast);
    font-weight: 600;
  }
  .actions button.run[data-abort="yes"]:not(:disabled) {
    border-color: var(--caos-color-danger);
    background: var(--caos-color-danger-fill);
    color: var(--caos-color-danger-contrast);
  }

  .empty { font-family: var(--caos-font); font-size: 13px; color: var(--caos-color-text-muted); }
`);

/** `Class.method` — the identity of one test, used everywhere a single test is named. */
export function testKey(className: string, method: string): string {
  return `${className}.${method}`;
}

/** How a class's box stands, given which of its methods are selected. */
export function classSelection(methods: readonly string[], selected: ReadonlySet<string>, className: string): CaosClassSelection {
  if (methods.length === 0) return 'none';
  let picked = 0;
  for (const m of methods) if (selected.has(testKey(className, m))) picked += 1;
  return picked === 0 ? 'none' : picked === methods.length ? 'all' : 'some';
}

export class CaosTestRunGrid extends CaosElement {
  static get observedAttributes(): string[] {
    return ['running', 'open-when-finished', 'skip-coverage', 'always-asynchronous', 'empty-text'];
  }

  private summaryEl: HTMLParagraphElement;
  private optionsEl: HTMLDivElement;
  private classesEl: HTMLDivElement;
  private runEl: HTMLButtonElement;
  private rerunEl: HTMLButtonElement;

  private data: readonly CaosTestClass[] = [];
  private picked = new Set<string>();
  private failedKeys: readonly string[] = [];

  constructor() {
    super(sheet);

    const wrap = document.createElement('div');
    wrap.className = 'grid';
    wrap.setAttribute('part', 'grid');

    this.summaryEl = document.createElement('p');
    this.summaryEl.className = 'summary';
    this.summaryEl.setAttribute('part', 'summary');
    this.summaryEl.setAttribute('role', 'status');

    this.optionsEl = document.createElement('div');
    this.optionsEl.className = 'options';
    this.optionsEl.setAttribute('part', 'options');

    this.classesEl = document.createElement('div');
    this.classesEl.className = 'classes';
    this.classesEl.setAttribute('part', 'classes');

    const actions = document.createElement('div');
    actions.className = 'actions';
    actions.setAttribute('part', 'actions');
    this.runEl = document.createElement('button');
    this.runEl.type = 'button';
    this.runEl.className = 'run';
    this.runEl.setAttribute('part', 'run');
    this.runEl.addEventListener('click', () => (this.hasAttribute('running') ? this.abort() : this.run()));
    this.rerunEl = document.createElement('button');
    this.rerunEl.type = 'button';
    this.rerunEl.setAttribute('part', 'rerun-failed');
    this.rerunEl.textContent = 'Select what failed';
    this.rerunEl.addEventListener('click', () => this.selectFailed());
    actions.append(this.runEl, this.rerunEl);

    wrap.append(this.summaryEl, this.optionsEl, this.classesEl, actions);
    this.root.append(wrap);
  }

  connectedCallback(): void {
    this.render();
  }

  attributeChangedCallback(): void {
    this.render();
  }

  get classes(): readonly CaosTestClass[] {
    return this.data;
  }

  set classes(next: readonly CaosTestClass[]) {
    this.data = Array.isArray(next) ? next : [];
    // A selection naming a test that is no longer there cannot be run, and would make the count at
    // the top say more than the run would actually contain.
    const known = new Set(this.data.flatMap((c) => c.methods.map((m) => testKey(c.name, m))));
    for (const key of [...this.picked]) if (!known.has(key)) this.picked.delete(key);
    this.render();
  }

  /** Which tests failed in the last run, as `Class.method` keys. Drives "select what failed". */
  get failed(): readonly string[] {
    return this.failedKeys;
  }

  set failed(next: readonly string[]) {
    this.failedKeys = Array.isArray(next) ? next : [];
    this.render();
  }

  /** The tests picked into the run, as `Class.method` keys, in the order the classes are listed. */
  get selected(): readonly string[] {
    const out: string[] = [];
    for (const c of this.data) for (const m of c.methods) if (this.picked.has(testKey(c.name, m))) out.push(testKey(c.name, m));
    return out;
  }

  set selected(next: readonly string[]) {
    this.picked = new Set(Array.isArray(next) ? next : []);
    this.render();
  }

  /** The three run options as they currently stand. */
  get options(): CaosTestRunOptions {
    return {
      openWhenFinished: this.hasAttribute('open-when-finished'),
      skipCoverage: this.hasAttribute('skip-coverage'),
      alwaysAsynchronous: this.hasAttribute('always-asynchronous'),
    };
  }

  /** How many methods there are in total — the denominator the summary states. */
  get totalMethods(): number {
    return this.data.reduce((n, c) => n + c.methods.length, 0);
  }

  private toggleMethod(className: string, method: string, on: boolean): void {
    const key = testKey(className, method);
    if (on) this.picked.add(key);
    else this.picked.delete(key);
    this.render();
    this.raiseSelection();
  }

  private toggleClass(c: CaosTestClass, on: boolean): void {
    for (const m of c.methods) {
      const key = testKey(c.name, m);
      if (on) this.picked.add(key);
      else this.picked.delete(key);
    }
    this.render();
    this.raiseSelection();
  }

  private selectFailed(): void {
    // REPLACES the selection rather than adding to it. "Re-run what failed" means what failed, and
    // quietly keeping fifty passing tests from the previous selection is the slow answer to the
    // question somebody asked to get a fast one.
    const known = new Set(this.data.flatMap((c) => c.methods.map((m) => testKey(c.name, m))));
    this.picked = new Set(this.failedKeys.filter((k) => known.has(k)));
    this.render();
    this.raiseSelection();
  }

  private raiseSelection(): void {
    this.dispatchEvent(
      new CustomEvent('caos-test-selection', { bubbles: true, composed: true, detail: { selected: this.selected } }),
    );
  }

  private run(): void {
    if (this.selected.length === 0) return;
    this.dispatchEvent(
      new CustomEvent('caos-test-run', {
        bubbles: true,
        composed: true,
        detail: { tests: this.selected, options: this.options },
      }),
    );
  }

  private abort(): void {
    this.dispatchEvent(new CustomEvent('caos-test-abort', { bubbles: true, composed: true }));
  }

  private render(): void {
    this.renderOptions();
    this.renderClasses();

    const picked = this.selected.length;
    const total = this.totalMethods;
    const strong = document.createElement('strong');
    strong.textContent = `${picked} of ${total} ${total === 1 ? 'test' : 'tests'} selected`;
    const nodes: Node[] = [strong];
    if (this.failedKeys.length > 0) {
      nodes.push(document.createTextNode(` · ${this.failedKeys.length} failed last run`));
    }
    this.summaryEl.replaceChildren(...nodes);

    const running = this.hasAttribute('running');
    this.runEl.textContent = running ? 'Abort run' : 'Run selected';
    // Abort is a real control, not the run button greyed out: a run that cannot be stopped is a run
    // people avoid starting.
    if (running) this.runEl.setAttribute('data-abort', 'yes');
    else this.runEl.removeAttribute('data-abort');
    this.runEl.disabled = !running && picked === 0;

    // Disabled rather than hidden with nothing failed: an absent control would read as "this run
    // had no failures" on a screen that has not run anything yet.
    this.rerunEl.disabled = this.failedKeys.length === 0 || running;
    this.rerunEl.title =
      this.failedKeys.length === 0 ? 'Nothing has failed yet' : `Select the ${this.failedKeys.length} that failed`;
  }

  private renderOptions(): void {
    const OPTIONS: ReadonlyArray<readonly [string, string]> = [
      ['open-when-finished', 'Open the run when it finishes'],
      ['skip-coverage', 'Skip code coverage'],
      ['always-asynchronous', 'Always run asynchronously'],
    ];
    this.optionsEl.replaceChildren(
      ...OPTIONS.map(([attr, label]) => {
        const wrap = document.createElement('label');
        wrap.className = 'option';
        wrap.setAttribute('part', 'option');
        const box = document.createElement('input');
        box.type = 'checkbox';
        box.checked = this.hasAttribute(attr);
        box.addEventListener('change', () => {
          if (box.checked) this.setAttribute(attr, '');
          else this.removeAttribute(attr);
          this.dispatchEvent(
            new CustomEvent('caos-test-options', { bubbles: true, composed: true, detail: this.options }),
          );
        });
        wrap.append(box, document.createTextNode(label));
        return wrap;
      }),
    );
  }

  private renderClasses(): void {
    if (this.data.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'empty';
      empty.textContent = this.getAttribute('empty-text') ?? 'No test classes found.';
      this.classesEl.replaceChildren(empty);
      return;
    }

    const failed = new Set(this.failedKeys);
    this.classesEl.replaceChildren(
      ...this.data.map((c) => {
        const section = document.createElement('div');
        section.className = 'class';
        section.setAttribute('part', 'class');

        const head = document.createElement('label');
        head.className = 'class-toggle';
        head.setAttribute('part', 'class-toggle');
        const box = document.createElement('input');
        box.type = 'checkbox';
        const state = classSelection(c.methods, this.picked, c.name);
        box.checked = state === 'all';
        // A partly-selected class is neither ticked nor empty, and saying so is the difference
        // between a hundred checkboxes and a control: `indeterminate` is what makes the class row
        // able to report what is under it.
        box.indeterminate = state === 'some';
        box.setAttribute('aria-label', `${c.name} — ${c.methods.length} tests`);
        box.addEventListener('change', () => this.toggleClass(c, box.checked));
        const count = document.createElement('span');
        count.className = 'count';
        count.textContent = `${c.methods.length}`;
        head.append(box, document.createTextNode(c.name), count);

        const methods = document.createElement('div');
        methods.className = 'methods';
        methods.setAttribute('part', 'methods');
        methods.append(
          ...c.methods.map((m) => {
            const row = document.createElement('label');
            row.className = 'method';
            row.setAttribute('part', 'method');
            const key = testKey(c.name, m);
            if (failed.has(key)) row.setAttribute('data-failed', 'yes');
            const mbox = document.createElement('input');
            mbox.type = 'checkbox';
            mbox.checked = this.picked.has(key);
            mbox.setAttribute('aria-label', failed.has(key) ? `${key} — failed last run` : key);
            mbox.addEventListener('change', () => this.toggleMethod(c.name, m, mbox.checked));
            row.append(mbox, document.createTextNode(m));
            return row;
          }),
        );

        section.append(head, methods);
        return section;
      }),
    );
  }
}

define('caos-test-run-grid', CaosTestRunGrid);

declare global {
  interface HTMLElementTagNameMap {
    'caos-test-run-grid': CaosTestRunGrid;
  }
}
